import { useFlags } from 'launchdarkly-react-client-sdk'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import api from 'api'
import {
  CACHE_TAG_ACCOUNTS_VERSION_LIST,
  CACHE_TAG_CONFIGURED_SERVICE_DETAILS,
  CACHE_TAG_CONFIGURED_SERVICES_LIST,
} from 'api/cacheTagTypes'
import { useGetLatestUnreadNotificationsQuery, useGetNotificationUnreadCountQuery } from 'api/notification-center/notificationCenter'
import { getWorkOrderListInvalidatesTags } from 'components/customer-details/content/work-orders/utils'
import { isWORedesignFeatureAvailable } from 'data/launch-darkly/featureSelectors'
import useDocumentVisible from 'hooks/useDocumentVisible'
import { getCustomerDetails } from 'middleware/actions/customers'
import { selectOptimizationJob, type OptimizationRootStateSlice } from 'slices/route/optimizationSlice'
import {
  NOTIFICATION_SOURCE,
  type Notification,
  type NotificationSource,
  type NotificationStatus,
} from 'types/modules/notification-center/notification'
import { get } from 'utils/lodash'

import { arrivalsIn, hasRunInFlight, isTerminalStatus, shouldToast } from './notificationArrivals'
import { pushNotificationToast } from './notificationCenterToast'
import { NOTIFICATION_POLLING, pollIntervalFor } from './notificationPolling'

const PROBE_PARAMS = Object.freeze({ size: NOTIFICATION_POLLING.probeSize })

/** Stable identity so an unresolved probe doesn't retrigger effects that depend on the list. */
const EMPTY_NOTIFICATIONS: readonly Notification[] = Object.freeze([])

const ACCOUNT_RUN_SOURCES: ReadonlySet<NotificationSource> = new Set([
  NOTIFICATION_SOURCE.ACCOUNT_DEACTIVATION,
  NOTIFICATION_SOURCE.ACCOUNT_REACTIVATION,
])

export type NotificationsSubscription = {
  unreadCount: number
  /** Newest unread as the server holds it — what the bell's preview lists. */
  latestUnread: readonly Notification[]
}

/**
 * Live delivery, v1: polling, since SSE is deferred and the transport stays behind this hook.
 * The count only drives the badge: a run transitions one row in place, so QUEUED → SUCCEEDED
 * leaves an unread row unread and the count never moves — hence the second query, which also
 * feeds the bell's list. A hidden tab stops polling; see `useDocumentVisible` for why not RTKQ's own.
 */
export const useNotificationsSubscription = (): NotificationsSubscription => {
  const isVisible = useDocumentVisible()
  const dispatch = useDispatch()
  const flags = useFlags()
  const isWORedesignEnabled = isWORedesignFeatureAvailable({ flags })
  const openAccountId = useSelector(state => get(state, 'CustomersReducer.customerDetails.id'))

  /** Row id → the status this tab last saw, so a re-poll of unchanged rows stays silent. */
  const seenRef = useRef<Map<string, NotificationStatus>>(new Map())
  const hasSeededRef = useRef(false)

  /** Held in state, not derived inline: it is read from the answer of the very poll it sets the rate for. */
  const [isRunInFlight, setIsRunInFlight] = useState(false)

  /**
   * Known at submit, where `isRunInFlight` must wait for a poll that already returned the run.
   * Windowed like a reported run, so a job orphaned by navigation cannot pin the fast rate.
   */
  const localRunStartedAt = useSelector((state: OptimizationRootStateSlice) => selectOptimizationJob(state)?.startedAt ?? null)
  const hasLocalRun = localRunStartedAt !== null && Date.now() - localRunStartedAt < NOTIFICATION_POLLING.inFlightWindowMs

  const pollingInterval = pollIntervalFor({ isVisible, isRunInFlight: isRunInFlight || hasLocalRun })

  const { data: unreadCount } = useGetNotificationUnreadCountQuery(undefined, {
    pollingInterval,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  })

  const { data: latestUnread } = useGetLatestUnreadNotificationsQuery(PROBE_PARAMS, {
    pollingInterval,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  })

  const notifications = latestUnread?.notifications ?? EMPTY_NOTIFICATIONS

  useEffect(() => {
    setIsRunInFlight(hasRunInFlight(notifications, Date.now()))
  }, [notifications])

  /**
   * A settled run already changed the data on screen, and this is the only place that learns it
   * finished. Deactivation and reactivation are the producers with app state behind them.
   */
  const refreshWhatSettled = useCallback(
    (notification: Notification) => {
      if (!ACCOUNT_RUN_SOURCES.has(notification.source) || !notification.entityId) return

      const accountId = notification.entityId
      // The helper is untyped JS, so every option has to be named — including the ones this call leaves out.
      const workOrderTags = getWorkOrderListInvalidatesTags({
        isNew: isWORedesignEnabled,
        sectionWise: true,
        serviceDate: undefined,
        woSectionOnly: undefined,
        csId: undefined,
      })

      dispatch(
        api.util.invalidateTags([
          { type: CACHE_TAG_ACCOUNTS_VERSION_LIST, id: accountId },
          CACHE_TAG_CONFIGURED_SERVICES_LIST,
          // The Services tab rows read each service's details; a run changes their status and dates.
          CACHE_TAG_CONFIGURED_SERVICE_DETAILS,
          ...workOrderTags,
        ])
      )
      // The account page reads through a saga rather than the cache, so a tag cannot reach it.
      if (openAccountId === accountId) dispatch(getCustomerDetails({ accountId }))
    },
    [dispatch, isWORedesignEnabled, openAccountId]
  )

  useEffect(() => {
    if (!latestUnread) return

    const arrivals = arrivalsIn(latestUnread.notifications, seenRef.current)
    // The first probe is this tab's starting picture, not a burst of events — recording it
    // silently is what keeps a page load from toasting the whole unread inbox at once.
    const isSeeding = !hasSeededRef.current
    hasSeededRef.current = true

    arrivals.forEach(notification => {
      if (!isSeeding) {
        if (shouldToast(notification, seenRef.current)) pushNotificationToast(notification)
        if (isTerminalStatus(notification.status)) refreshWhatSettled(notification)
      }
      seenRef.current.set(notification.id, notification.status)
    })
  }, [latestUnread, refreshWhatSettled])

  return { unreadCount: unreadCount ?? 0, latestUnread: notifications }
}
